// Copyright (c) Microsoft. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IChatRoom } from '../../../libs/models/conversations/ChatRoom';
import {
  // ConversationInputChange,
  Conversations,
  ContinuationToken,
  ConversationsState,
  // ConversationSystemDescriptionChange,
  ConversationTitleChange,
  initialState,
  UpdateBotStatePayload,
} from '../../../libs/models/conversations/ConversationsState';
import {
  IChatMessage,
  IChatMessageList,
  IChatStreamingMessage,
  IChatStreamingProgressMessage,
} from '../../../libs/models/conversations/ChatMessage';
import { IFileAnalyzedCacheResult } from '../../../libs/models/conversations/File';
import { WebSearchSource } from '../../../libs/models/app/Plugin';
import { IMCPTool } from '../../../libs/models/app/mcp/Tool';

export const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setConversations: (state: ConversationsState, action: PayloadAction<Conversations>) => {
      state.conversations = { ...state.conversations, ...action.payload };
    },
    setCountinuationToken: (state: ConversationsState, action: PayloadAction<ContinuationToken>) => {
      state.continuationToken = action.payload;
    },
    editConversationTitle: (state: ConversationsState, action: PayloadAction<ConversationTitleChange>) => {
      const id = action.payload.id;
      const newTitle = action.payload.newTitle;
      state.conversations[id].title = newTitle;
      // 24.07.11 이름변경시 순서 이동 삭제
      // frontLoadChat(state, id);
    },
    // editConversationInput: (state: ConversationsState, action: PayloadAction<ConversationInputChange>) => {
    //     const id = action.payload.id;
    //     const newInput = action.payload.newInput;
    //     state.conversations[id].input = newInput;
    // },
    // editConversationSystemDescription: (
    //     state: ConversationsState,
    //     action: PayloadAction<ConversationSystemDescriptionChange>,
    // ) => {
    //     const id = action.payload.id;
    //     const newSystemDescription = action.payload.newSystemDescription;
    //     state.conversations[id].systemDescription = newSystemDescription;
    // },
    // editConversationMemoryBalance: (
    //     state: ConversationsState,
    //     action: PayloadAction<{ id: string; memoryBalance: number }>,
    // ) => {
    //     const id = action.payload.id;
    //     const newMemoryBalance = action.payload.memoryBalance;
    //     state.conversations[id].memoryBalance = newMemoryBalance;
    // },
    setSelectedConversation: (state: ConversationsState, action: PayloadAction<string>) => {
      state.selectedId = action.payload;
    },
    leaveConversation: (state: ConversationsState) => {
      // state.conversations[state.selectedId].messages.data = [] as IChatMessage[];
      state.conversations[state.selectedId].messages.continuationToken = null;
      // state.selectedId = '';
    },
    setChatMessages: (state: ConversationsState, action: PayloadAction<{ id: string; messages: IChatMessageList }>) => {
      const { id: chatroomId, messages: messages } = action.payload;
      //if(!state.conversations[chatroomId].botResponseStatus){
        state.conversations[chatroomId].messages.data = messages.data;
        state.conversations[chatroomId].messages.continuationToken = messages.continuationToken;
      //}
    },
    addChatMessages: (state: ConversationsState, action: PayloadAction<{ id: string; messages: IChatMessageList }>) => {
      const { id: chatroomId, messages: messages } = action.payload;
      state.conversations[chatroomId].messages.data.unshift(...messages.data);
      state.conversations[chatroomId].messages.continuationToken = messages.continuationToken;
    },
    addPluginMessage: (state: ConversationsState, action: PayloadAction<{ message: IChatMessage }>) => {
      const { message: message } = action.payload;
      state.conversations[message.chatRoomId].messages.data.push(message);
    },
    // toggleMultiUserConversations: (state: ConversationsState) => {
    //     const keys = Object.keys(state.conversations);
    //     keys.forEach((key) => {
    //         if (state.conversations[key].users.length > 1) {
    //             state.conversations[key].hidden = !state.conversations[key].hidden;
    //         }
    //     });
    // },
    addConversation: (state: ConversationsState, action: PayloadAction<IChatRoom>) => {
      const newId = action.payload.id;
      state.conversations = { [newId]: action.payload, ...state.conversations };
      state.selectedId = newId;
    },
    // addUserToConversation: (
    //     state: ConversationsState,
    //     action: PayloadAction<{ user: IChatUser; chatId: string }>,
    // ) => {
    //     const { user, chatId } = action.payload;
    //     state.conversations[chatId].users.push(user);
    //     state.conversations[chatId].userDataLoaded = false;
    // },
    // setImportingDocumentsToConversation: (
    //     state: ConversationsState,
    //     action: PayloadAction<{ importingDocuments: string[]; chatId: string }>,
    // ) => {
    //     const { importingDocuments, chatId } = action.payload;
    //     state.conversations[chatId].importingDocuments = importingDocuments;
    // },
    // setUsersLoaded: (state: ConversationsState, action: PayloadAction<string>) => {
    //     state.conversations[action.payload].userDataLoaded = true;
    // },
    /*
     * romServer() both update the conversations state.
     * However they are for different purposes. The former action is for updating the conversation from the
     * webapp and will be captured by the SignalR middleware and the payload will be broadcasted to all clients
     * in the same group.
     * The addMessageToConversationFromServer() action is triggered by the SignalR middleware when a response is received
     * from the webapi.
     */
    addMessageToConversationFromUser: (
      _state: ConversationsState,
      _action: PayloadAction<{
        message: string;
        chatId: string;
        model: string;
        plugin?: string;
        attachFiles?: IFileAnalyzedCacheResult[];
        cacheMode?: boolean;
        isDraft?: boolean;
        webSearchOn?: boolean;
        webSearchSource?: WebSearchSource,
        mcpTools?: IMCPTool[];
        agentMode?: boolean;
      }>,
    ) => {
      // const { message, chatId } = action.payload;
      // updateConversation(state, chatId, message);
    },

    addMessageToConversationFromServer: (
      state: ConversationsState,
      action: PayloadAction<{ message: IChatMessage }>,
    ) => {
      const { message } = action.payload;
      clearMessageConversation(state, message.id);
      updateConversation(state, message.chatRoomId, message);
    },
    addStreamingMessageToConversationFromServer: (
      state: ConversationsState,
      action: PayloadAction<{ message: IChatStreamingMessage }>,
    ) => {
      const { message } = action.payload;
      updateStreamingConversation(state, message);
    },
    clearMessageToConversationFromServer: (state: ConversationsState, action: PayloadAction<{ id: string }>) => {
      const { id: chatId } = action.payload;
      clearMessageConversation(state, chatId);
    },
    retryMessageToConversationFromUser: (
      _state: ConversationsState,
      _action: PayloadAction<{ message: IChatMessage; model: string; plugin: string, mcpTools: IMCPTool[] }>,
    ) => {},
    /*
     * updateUserIsTyping() and updateUserIsTypingFromServer() both update a user's typing state.
     * However they are for different purposes. The former action is for updating an user's typing state from
     * the webapp and will be captured by the SignalR middleware and the payload will be broadcasted to all clients
     * in the same group.
     * The updateUserIsTypingFromServer() action is triggered by the SignalR middleware when a state is received
     * from the webapi.
     */
    updateUserIsTyping: (state: ConversationsState, action: PayloadAction<{ chatId: string; isTyping: boolean }>) => {
      const { chatId, isTyping } = action.payload;
      updateUserTypingState(state, chatId, isTyping);
    },
    // updateUserIsTypingFromServer: (
    //     state: ConversationsState,
    //     action: PayloadAction<{ userId: string; chatId: string; isTyping: boolean }>,
    // ) => {
    //     const { userId, chatId, isTyping } = action.payload;
    //     updateUserTypingState(state, userId, chatId, isTyping);
    // },
    updateBotResponseStatus: (state: ConversationsState, action: PayloadAction<UpdateBotStatePayload>) => {
      const { chatRoomId, isActive: status } = action.payload;
      const id = chatRoomId ? chatRoomId : state.selectedId;
      const conversation = state.conversations[id];
      // popMessageContent(conversation, status);
      conversation.botResponseStatus = status;
    },
    updateBotResponseProgress: (state: ConversationsState, action: PayloadAction<IChatStreamingProgressMessage>) => {
      const { chatId, chatItemId, progress, searchResult } = action.payload;
      const id = state.selectedId;
      const conversation = state.conversations[id];
      const index = state.conversations[state.selectedId].messages.data.findIndex((m) => m.id == chatId);
      if (index > -1) {
        conversation.messages.data[index].messages[chatItemId].progress = progress;
        if(searchResult) conversation.messages.data[index].messages[chatItemId].searchResult = searchResult;
      }
    },
    stopStreaming: () => {},
    // updateMessageProperty: <K extends keyof IChatMessage, V extends IChatMessage[K]>(
    //     state: ConversationsState,
    //     action: PayloadAction<{
    //         property: K;
    //         value: V;
    //         chatId: string;
    //         messageIdOrIndex: string | number;
    //         updatedContent?: string;
    //         frontLoad?: boolean;
    //     }>,
    // ) => {
    //     const { property, value, messageIdOrIndex, chatId, updatedContent, frontLoad } = action.payload;
    //     const conversation = state.conversations[chatId];
    //     const conversationMessage =
    //         typeof messageIdOrIndex === 'number'
    //             ? conversation.messages[messageIdOrIndex]
    //             : conversation.messages.find((m) => m.id === messageIdOrIndex);

    //     if (conversationMessage) {
    //         conversationMessage[property] = value;
    //         if (updatedContent) {
    //             conversationMessage.content = updatedContent;
    //         }
    //     }

    //     if (frontLoad) {
    //         frontLoadChat(state, chatId);
    //     }
    // },
    deleteConversation: (state: ConversationsState, action: PayloadAction<IChatRoom>) => {
      const id = action.payload.id;
      const { [id]: _, ...rest } = state.conversations;
      state.conversations = rest;
    },

    resetConversation(state, action: PayloadAction<string>) {
      const chatroomId = action.payload;
      if (state.conversations[chatroomId]) state.conversations[chatroomId].messages.data = [];
    },
    // disableConversation: (state: ConversationsState, action: PayloadAction<string>) => {
    //     const id = action.payload;
    //     state.conversations[id].disabled = true;
    //     frontLoadChat(state, id);
    //     return;
    // },
    // updatePluginState: (state: ConversationsState, action: PayloadAction<UpdatePluginStatePayload>) => {
    //     const { id, pluginName, newState } = action.payload;
    //     const isPluginEnabled = state.conversations[id].enabledHostedPlugins.find((p) => p === pluginName);
    //     if (newState) {
    //         if (isPluginEnabled) {
    //             return;
    //         }
    //         state.conversations[id].enabledHostedPlugins.push(pluginName);
    //     } else {
    //         if (!isPluginEnabled) {
    //             return;
    //         }
    //         state.conversations[id].enabledHostedPlugins = state.conversations[id].enabledHostedPlugins.filter(
    //             (p) => p !== pluginName,
    //         );
    //     }
    // },
  },
});

// 24.07.11 이름 변경시 상위로 이동 삭제
// const frontLoadChat = (state: ConversationsState, id: string) => {
//   const conversation = state.conversations[id];
//   const { [id]: _, ...rest } = state.conversations;
//   state.conversations = { [id]: conversation, ...rest };
// };

const updateConversation = (state: ConversationsState, chatId: string, message: IChatMessage) => {
  const index = state.conversations[chatId].messages.data.findIndex((m) => m.id == chatId);
  if(index > -1){
    state.conversations[chatId].messages.data[index] = message;
  }else{
    state.conversations[chatId].messages.data.push(message);
  }
  
  // frontLoadChat(state, chatId);
};

const updateStreamingConversation = (state: ConversationsState, stream: IChatStreamingMessage) => {
  const index = state.conversations[stream.chatRoomId].messages.data.findIndex((m) => m.id == stream.chatId);
  if (index > -1) {
    const message = state.conversations[stream.chatRoomId].messages.data[index].messages[stream.chatItemId];
    if (stream.hasError) {
      message.hasError = stream.hasError;
      message.errorMessage = stream.errorMessage;
    } else {
      if (stream.message)
        message.message += stream.message;
      if (stream.references)
        message.references = stream.references;
      if (stream.searchResult)
        message.searchResult = stream.searchResult;
      if (stream.functionResult) {
        (message.functionResults ??= []).push(stream.functionResult);
      }
    }
  }
};

const updateUserTypingState = (state: ConversationsState, chatId: string, isTyping: boolean) => {
  const conversation = state.conversations[chatId];
  conversation.isTyping = isTyping;
};

// const popMessageContent = (chatRoom: IChatRoom, status: boolean) => {
//     const message = chatRoom.messages.data;
//     if(chatRoom.botResponseStatus && !status){
//         if(message[message.length-1].message.length === 0) message.pop();
//     }
// }

const clearMessageConversation = (state: ConversationsState, chatId: string) => {
  const index = state.conversations[state.selectedId].messages.data.findIndex((c) => c.id == chatId);
  if (index > -1) state.conversations[state.selectedId].messages.data.splice(index, 1);
};

export const {
  setConversations,
  setCountinuationToken,
  // editConversationTitle,
  // editConversationInput,
  // editConversationSystemDescription,
  // editConversationMemoryBalance,
  setSelectedConversation,
  // toggleMultiUserConversations,
  addConversation,
  // setImportingDocumentsToConversation,
  addMessageToConversationFromUser,
  addMessageToConversationFromServer,
  addStreamingMessageToConversationFromServer,
  // updateMessageProperty,
  updateUserIsTyping,
  // updateUserIsTypingFromServer,
  updateBotResponseStatus,
  // setUsersLoaded,
  deleteConversation,
  resetConversation,
  // disableConversation,
  // updatePluginState,
  leaveConversation,
  addChatMessages,
  setChatMessages,
  addPluginMessage,
  stopStreaming,
  clearMessageToConversationFromServer,
  retryMessageToConversationFromUser,
  updateBotResponseProgress,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
